"use client";

import "./sales-follow-up-cta-attention.css";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { track } from "@vercel/analytics";
import { CalendarClock, Loader2, SendHorizontal } from "lucide-react";
import { reportError } from "@/lib/report-error";
import { cn } from "@/lib/utils";
import { useSession } from "@/hooks/use-session";

type SalesFollowUpCardProps = {
  /** From HuntView when session SWR is still settling — server still authorizes POST. */
  userEmail?: string | null;
};

export function SalesFollowUpCard({ userEmail }: SalesFollowUpCardProps) {
  const { session, mutate } = useSession();
  const [busy, setBusy] = useState(false);

  const email = session?.email?.trim() || userEmail?.trim();
  if (!email) {
    return null;
  }

  const sent = Boolean(session?.follow_up_sent_at);

  const handleClick = async () => {
    if (sent || busy) return;
    setBusy(true);
    try {
      const response = await fetch("/api/sales-follow-up", { method: "POST" });
      const data = (await response.json().catch(() => ({}))) as { error?: string };

      if (response.status === 409) {
        await mutate();
        return;
      }

      if (response.status === 503) {
        reportError(new Error(data.error || "Sales follow-up not configured"), {
          component: "SalesFollowUpCard",
          action: "handleClick",
        });
        setBusy(false);
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || "Request failed");
      }

      track("sales_follow_up_requested");
      await mutate();
    } catch (err) {
      reportError(err, { component: "SalesFollowUpCard", action: "handleClick" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="rounded-2xl border-2 border-dashed border-violet-500/40 bg-violet-500/5">
      <CardHeader className="pb-2">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-500/15">
            <CalendarClock className="h-5 w-5 text-violet-700 dark:text-violet-400" />
          </div>
          <div className="min-w-0 flex-1">
            <CardTitle className="text-lg leading-tight">Request a follow-up</CardTitle>
            <CardDescription className="mt-1 text-sm leading-relaxed">
              Request a follow-up from the FluidityIQ sales team to get more information or to schedule a demo.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Button
          type="button"
          onClick={() => void handleClick()}
          disabled={sent || busy}
          variant="outline"
          className={cn(
            "h-12 w-full rounded-xl text-base font-medium text-violet-950 dark:text-violet-100",
            sent
              ? "border-muted-foreground/30 bg-muted/30 text-muted-foreground opacity-80"
              : "border-violet-500/40 hover:bg-violet-500/10",
            !sent && !busy && "relative! overflow-hidden border-transparent"
          )}
        >
          {busy ? (
            <>
              <Loader2 className="mr-2 size-6 shrink-0 animate-spin" strokeWidth={1.5} />
              Sending…
            </>
          ) : sent ? (
            "Request sent"
          ) : (
            <>
              <span aria-hidden className="sales-follow-up-cta-attention-layer" />
              <span aria-hidden className="sales-follow-up-cta-border-layer" />
              <span className="sales-follow-up-cta-inner inline-flex items-center">
                <SendHorizontal className="mr-2 size-7 shrink-0" strokeWidth={1.5} />
                Request follow-up
              </span>
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
